import { Button, ButtonGroup, Checkbox, IconButton, Icons, Stack, ToggleButtonGroup, Typography } from '@healthinal/ui';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { addDays, format, getDay, isToday, parse, startOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Calendar, Event as CalendarEvent, dateFnsLocalizer, SlotInfo, ToolbarProps, View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  getAppointmentEndDateTimeOrStart,
  getAppointmentStartDateTime,
  getAppointmentTimeFrom,
} from '../../api/created/appointment.ts';
import { useGetAppointments } from '../../api/generated/appointment-controller/appointment-controller.ts';
import { AppointmentDtoType } from '../../api/generated/model';
import { ContentSection } from '../../components/ContentSection.tsx';
import { theme } from '../../theme.ts';
import { getAppointmentTypeColor, getAppointmentTypeName } from '../../utils/appointment.ts';
import { formatDateForInput, formatDateYear, isTodayOrAfter } from '../../utils/date.ts';
import { AppointmentDetailsDialog } from '../components/appointment/AppointmentDetailsDialog.tsx';
import { CreateAppointmentDialog } from '../components/appointment/CreateAppointmentDialog.tsx';

interface SearchParams {
  appointmentId: string | undefined;
}

export const Route = createFileRoute('/appointments/')({
  component: Appointments,
  validateSearch: (search): SearchParams => {
    return {
      appointmentId: search?.appointmentId as string,
    };
  },
});

function Appointments() {
  const { appointmentId: appointmentIdFromSearchParam } = Route.useSearch();
  const [isCreateAppointmentDialogShown, setIsCreateAppointmentDialogShown] = useState(false);
  const [preselectedDate, setPreselectedDate] = useState<{ start: Date; end: Date } | undefined>();
  const [eventTypeFilter, setEventTypeFilter] = useState(new Set(Object.values(AppointmentDtoType)));
  const [range, setRange] = useState<{ start: Date; end: Date }>({
    start: addDays(new Date(), -5),
    end: addDays(new Date(), 5),
  });

  const { data: appointments } = useGetAppointments({
    from: formatDateForInput(range.start),
    until: formatDateForInput(range.end),
  });

  const events: CalendarEventWithType[] =
    appointments
      ?.filter((a) => eventTypeFilter.has(a.type))
      .map((a) => ({
        title: `${a.patient.lastName} ${a.patient.firstName} (${formatDateYear(a.patient.birthDate)}) ${getAppointmentTypeName(a.type)}`,
        start: new Date(getAppointmentStartDateTime(a) ?? ''),
        end: new Date(getAppointmentEndDateTimeOrStart(a) ?? ''),
        allDay: !getAppointmentTimeFrom(a),
        type: a.type,
        appointmentId: a.id,
        isAttended: a.isAttended,
      })) ?? [];

  const handleRangeChange = (range: { start: Date; end: Date } | Date[]) => {
    let start: Date | null, end: Date | null;
    if (Array.isArray(range)) {
      start = range[0];
      end = range[range.length - 1];
    } else {
      start = range.start;
      end = range.end;
    }

    if (start && end) {
      setRange({ start, end });
    }
  };

  const handleSelectSlot = useCallback(({ action, start, end }: SlotInfo) => {
    if (action === 'select' && isTodayOrAfter(start)) {
      setPreselectedDate({ start, end });
      setIsCreateAppointmentDialogShown(true);
    }
  }, []);

  const handleSelectingSlot = useCallback(({ start }: { start: Date }) => {
    return isTodayOrAfter(start) || false;
  }, []);

  const handleFilterCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setEventTypeFilter((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(value as AppointmentDtoType);
      } else {
        newSet.delete(value as AppointmentDtoType);
      }
      return newSet;
    });
  };

  const [appointmentIdForDetailView, setAppointmentIdForDetailView] = useState<string | null>(null);

  useEffect(() => {
    if (appointmentIdFromSearchParam && !appointmentIdForDetailView) {
      setAppointmentIdForDetailView(appointmentIdFromSearchParam);
    }
  }, [appointmentIdFromSearchParam]);

  return (
    <ContentSection>
      <Stack direction="row" alignItems="start" justifyContent="space-between">
        <Typography level="h1">Termine</Typography>
        <Button
          endDecorator={<Icons.Add />}
          onClick={() => {
            setPreselectedDate(undefined);
            setIsCreateAppointmentDialogShown(true);
          }}>
          Termin erstellen
        </Button>
      </Stack>
      <Stack direction={'row'} gap={2}>
        <Stack flex={1} p={1}>
          <Typography level={'title-md'}>Filter</Typography>
          <Stack gap={2} px={1} py={2}>
            {Object.values(AppointmentDtoType).map((item) => (
              <Checkbox
                key={item}
                value={item}
                label={getAppointmentTypeName(item)}
                color={getAppointmentTypeColor(item)}
                onChange={handleFilterCheckbox}
                checked={eventTypeFilter.has(item)}
              />
            ))}
          </Stack>
        </Stack>
        <Stack flex={5} sx={{ height: '80vh', overflow: 'auto' }}>
          <Calendar
            events={events}
            localizer={localizer}
            defaultView={defaultView}
            messages={messages}
            step={30}
            timeslots={1}
            min={new Date(2024, 0, 1, 7, 0, 0)} // sets the boundaries of shown time rows, date is ignored
            max={new Date(2024, 0, 1, 18, 30, 0)}
            views={{ month: true, week: true, work_week: true, day: true }}
            dayLayoutAlgorithm={'no-overlap'}
            eventPropGetter={eventPropGetter}
            dayPropGetter={dayPropGetter}
            onSelectSlot={handleSelectSlot}
            onSelecting={handleSelectingSlot}
            onRangeChange={handleRangeChange}
            selectable
            components={{
              toolbar: CalendarToolbar,
            }}
            onSelectEvent={(event) => setAppointmentIdForDetailView(event.appointmentId)}
          />
        </Stack>
      </Stack>

      <CreateAppointmentDialog
        preselectedDate={preselectedDate}
        open={isCreateAppointmentDialogShown}
        onClose={() => setIsCreateAppointmentDialogShown(false)}
      />
      <Outlet />
      {appointmentIdForDetailView && (
        <AppointmentDetailsDialog
          open={!!appointmentIdForDetailView}
          onClose={() => setAppointmentIdForDetailView(null)}
          appointmentId={appointmentIdForDetailView}
        />
      )}
    </ContentSection>
  );
}

function CalendarToolbar({ label, onNavigate, onView }: ToolbarProps<CalendarEventWithType>) {
  const [activeView, setActiveView] = useState<View>(defaultView);

  return (
    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1} my={1}>
      <Stack direction={'row'}>
        <ButtonGroup>
          <IconButton onClick={() => onNavigate('TODAY')} title={'Heute'}>
            <Icons.Today />
          </IconButton>
          <IconButton onClick={() => onNavigate('PREV')} title={'Zurück'}>
            <Icons.NavigateBefore />
          </IconButton>
          <IconButton onClick={() => onNavigate('NEXT')} title={'Nächste'}>
            <Icons.NavigateNext />
          </IconButton>
        </ButtonGroup>
      </Stack>
      <Typography level={'title-md'} flex={1}>
        {label}
      </Typography>
      <Stack direction={'row'} gap={1}>
        <ToggleButtonGroup
          value={activeView}
          onChange={(event, newValue) => {
            if (newValue) {
              setActiveView(newValue);
              onView(newValue);
            }
          }}>
          <Button value="month">Monat</Button>
          <Button value="week">Woche</Button>
          <Button value="work_week">Arbeitswoche</Button>
          <Button value="day">Tag</Button>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
}

// sets the color of each appointment
const eventPropGetter = (event: CalendarEventWithType) => {
  const eventColorName = getAppointmentTypeColor(event.type);
  const backgroundColor = theme.palette[eventColorName].plainColor;
  const color = theme.vars.palette.common.white;

  return {
    style: {
      backgroundColor,
      color,
      ...(event.isAttended === false && { textDecoration: 'line-through', textDecorationColor: 'lightgrey' }),
    },
  };
};

// sets the color of the current day
const dayPropGetter = (date: Date) => {
  if (isToday(date)) {
    return {
      style: {
        backgroundColor: theme.vars.palette.primary.softBg,
      },
    };
  }
  return {};
};

interface CalendarEventWithType extends CalendarEvent {
  type: AppointmentDtoType;
  appointmentId: string;
  isAttended: boolean | null | undefined;
}

const locales = {
  de: de,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const messages = {
  date: 'Datum',
  time: 'Zeit',
  event: 'Termin',
  noEventsInRange: 'Keine Termine in diesem Zeitraum',
};

const defaultView: View = 'work_week';
